import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PageTitle from '../components/PageTitle';
import { API_ENDPOINTS } from '../config/api';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    message: '',
    contactType: 'sales'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return false; // default to light for contact page
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Check for dark mode preference using the app's custom system
    const checkDarkMode = () => {
      const storedTheme = localStorage.getItem("theme");
      const isDark = storedTheme ? storedTheme === "dark" : false; // default to light for contact page
      setIsDarkMode(isDark);
      setDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for changes to localStorage (when user toggles theme in navbar)
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        checkDarkMode();
      }
    };

    // Listen for changes to data-bs-theme attribute
    const observer = new MutationObserver(() => {
      checkDarkMode();
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-bs-theme']
    });

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, [scrolled]);

  // Theme toggle handler
  const handleThemeToggle = () => {
    setDarkMode(prev => !prev);
  };

  // Handle theme changes
  useEffect(() => {
    document.body.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    setIsDarkMode(darkMode);
  }, [darkMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);
    try {
      const res = await fetch(API_ENDPOINTS.CONTACT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.message || 'Failed to send your message.');
        setIsSubmitting(false);
        return;
      }
      setSubmitSuccess(true);
      setFormData({
        firstName: '', lastName: '', email: '', phone: '', organization: '', role: '', message: '', contactType: 'sales'
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const navbarShouldBeScrolled = scrolled || navbarExpanded;

  return (
    <div className="contact-page">
      <PageTitle title="Contact Us" description="Get in touch with the MedHive team for support and inquiries" />
      {/* Navigation Bar */}
      <nav className={`navbar custom-navbar navbar-expand-lg navbar-light fixed-top${navbarShouldBeScrolled ? ' scrolled' : ''}`}
        style={{ zIndex: 1050 }}
      >
        <div className="container">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/">
            <img src={isDarkMode ? "/images/asset4.svg" : "/images/asset5.svg"} alt="brand logo" style={{ height: "16px"}}/>

            {/*<img
              src={navbarShouldBeScrolled ? "/images/logo5.svg" : "/images/logo4.svg"}
              alt="brand logo"
              style={{ width: "32px", height: "32px", marginTop: "-4px" }}
            />
            <span className="ms-2">medhive</span>*/}
          </Link>
          <div className="d-flex align-items-center ms-auto order-lg-2">
            <button
              className="btn btn-light-lite px-3"
              onClick={handleThemeToggle}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? (
                <i className="bi bi-sun-fill"></i>
              ) : (
                <i className="bi bi-moon-fill text-dark"></i>
              )}
            </button>
            <Link to="/login" className="btn btn-sm btn-primary ms-2">Login</Link>
            <button
              className="navbar-toggler btn-sm ms-2"
              type="button"
              aria-controls="mainNavbar"
              aria-expanded={navbarExpanded}
              aria-label="Toggle navigation"
              onClick={() => setNavbarExpanded((prev) => !prev)}
            >
              <span><i className={navbarShouldBeScrolled ? "bi bi-list" : "bi bi-list opacity-50"}></i></span>
            </button>
          </div>
          <div className={`collapse navbar-collapse justify-content-center order-lg-1${navbarExpanded ? ' show' : ''}`} id="mainNavbar">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4 gap-2 align-items-lg-center text-center">
              <li className="nav-item">
                <Link to="/" className="nav-link" onClick={() => setNavbarExpanded(false)}>Home</Link>
              </li>
              <li className="nav-item">
                <a href="#features" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Features</a>
              </li>
              <li className="nav-item">
                <a href="#pricing" className="nav-link" onClick={e => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); setNavbarExpanded(false); }}>Pricing</a>
              </li>
              <li className="nav-item">
                <Link to="/about" className="nav-link" onClick={() => setNavbarExpanded(false)}>About Us</Link>
              </li>
              <li className="nav-item">
                <Link to="/contact" className="nav-link" onClick={() => setNavbarExpanded(false)}>Contact Us</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      {/* Header Section */}
      <section className="contact-header-section">
        {/* Hero Background Elements */}
        <div className="hero-background-elements">
          <div className="hero-gradient-orb orb-1"></div>
          <div className="hero-gradient-orb orb-2"></div>
          <div className="hero-gradient-orb orb-3"></div>
        </div>
        <div className="container">
          <div className="row align-items-center" style={{ minHeight: '220px' }}>
            <div className="col-lg-12 text-center">
              <h1 className="display-4 fw-bold mb-3 gradient-text">Contact Us</h1>
              <p className="lead text-muted">Ready to transform your medical education program? Get in touch to learn how medhive can help.</p>
            </div>
          </div>
        </div>
      </section>
      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-8">
              <div className="contact-form-container mt-5 mb-5">
                <h2 className="mb-1 fw-bold">Get in touch for <span className="hero-title-highlight">support and inquiries</span></h2>              
                <p className="mb-4 text-muted ">
                  Whether you're interested in our platform, need technical support, 
                  or have questions about implementation, we're here to help.
                </p>
                
                {submitSuccess && (
                  <div className="alert alert-success" role="alert">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Thank you for your message! We'll get back to you within 24 hours.
                  </div>
                )}
                {submitError && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-x-circle-fill me-2"></i>
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="First Name"
                          required
                        />
                        <label htmlFor="firstName">First Name *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Last Name"
                          required
                        />
                        <label htmlFor="lastName">Last Name *</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Email Address"
                          required
                        />
                        <label htmlFor="email">Email Address *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Phone Number"
                        />
                        <label htmlFor="phone">Phone Number</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          id="organization"
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Organization"
                          required
                        />
                        <label htmlFor="organization">Organization *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <select
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select your role</option>
                          <option value="program-director">Program Director</option>
                          <option value="chief-resident">Chief Resident</option>
                          <option value="attending-physician">Attending Physician</option>
                          <option value="resident">Resident</option>
                          <option value="administrator">Administrator</option>
                          <option value="other">Other</option>
                        </select>
                        <label htmlFor="role">Your Role *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <select
                          id="contactType"
                          name="contactType"
                          value={formData.contactType}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="sales">Sales Inquiry</option>
                          <option value="early-access">Early Access</option>
                          <option value="technical-support">Technical Support</option>
                          <option value="demo">Request Demo</option>
                          <option value="partnership">Partnership</option>
                          <option value="other">Other Inquiry</option>
                        </select>
                        <label htmlFor="contactType">Contact Type *</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Tell us about your needs and how we can help..."
                          style={{ height: '120px' }}
                          required
                        ></textarea>
                        <label htmlFor="message">Message *</label>
                      </div>
                    </div>
                    <div className="col-12 d-flex justify-content-end
                    ">
                      <button
                        type="submit"
                        className="main-btn main-btn-lg main-primary-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            
            <div className="col-lg-4">
              <div className="rounded-3 p-md-5 p-4 bg-light-card h-100">
                <h5 className="mb-1 fw-semibold">Contact Information</h5>
                <p className="text-muted small">Other ways to get in touch</p>
                
                <div className="d-flex gap-3 contact-method pt-4">
                  <div className="contact-icon bg-primary-solid">
                    <i className="bi bi-telephone-fill"></i>
                  </div>
                  <div className="contact-details">
                    <h6 className="fw-semibold">Phone</h6>
                    <p>(575) 571-6460</p>
                    <p className="text-muted">Mon-Fri: 9AM-6PM EST</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-icon bg-orange-solid">
                    <i className="bi bi-envelope-fill"></i>
                  </div>
                  <div className="contact-details">
                    <h6 className="fw-semibold">Email</h6>
                    <p>support@medhiive.com</p>
                    <p className="text-muted">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-icon bg-success-solid">
                    <i className="bi bi-geo-alt-fill"></i>
                  </div>
                  <div className="contact-details">
                    <h6 className="fw-semibold">Office</h6>
                    <p>to be determined</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-icon bg-info-solid">
                    <i className="bi bi-clock-fill"></i>
                  </div>
                  <div className="contact-details">
                    <h6 className="fw-semibold">Support Hours</h6>
                    <p>24/7 Technical Support</p>
                    <p className="text-muted">Emergency support available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sales Section */}
      {/*<section className="sales-section">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-6">
              <div className="sales-content">
                <h2 className="gradient-text mb-4">Sales & Enterprise</h2>
                <p className="lead mb-4">
                  Ready to scale your medical education program? Our enterprise solutions 
                  are designed for large healthcare institutions with complex needs.
                </p>
                <div className="sales-features">
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Custom implementation and training</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Dedicated account management</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>API integration capabilities</span>
                  </div>
                  <div className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>White-label solutions available</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Link to="/demo" className="btn btn-primary btn-lg me-3">Request Demo</Link>
                  <Link to="/pricing" className="btn btn-outline-primary btn-lg">View Pricing</Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="sales-image">
                <div className="image-card">
                  <i className="bi bi-graph-up"></i>
                  <h4>Enterprise Solutions</h4>
                  <p>Scalable solutions for large healthcare institutions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="mb-1 fw-bold">Frequently Asked <span className="hero-title-highlight">Questions</span></h2>
            <p className="mb-4 text-muted ">Common questions about our platform and services</p>
          </div>
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="faq-item">
                <h6 className="fw-semibold">How quickly can we implement medhive?</h6>
                <p>Most organizations can be up and running within 2-4 weeks. We provide comprehensive training and support throughout the implementation process.</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="faq-item">
                <h5>What kind of support do you provide?</h5>
                <p>We offer 24/7 technical support, dedicated account management for enterprise clients, and comprehensive training programs for all users.</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="faq-item">
                <h5>Is medhive HIPAA compliant?</h5>
                <p>Yes, medhive is fully HIPAA compliant and follows all healthcare data security standards. We undergo regular security audits and certifications.</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="faq-item">
                <h5>Can we integrate with existing systems?</h5>
                <p>Absolutely! We offer API integrations with most major EHR systems and can work with your existing infrastructure to ensure seamless operation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage; 